import Insight from "../insightClass";
import React from "react";
import { insightCBAMMedia } from "../../../assets/assetReference";
import { ContactSubscribe } from "../../../components/Contact/Contact";
export const insight069 = new Insight(
  "09 October 2025",
  {
    pageTitle: "CBAM: regulations and compliance for the offshore wind sector",
    metaDescription:
      "WeBridge Insights, CBAM: regulations and compliance for the offshore wind sector",
    keyWords:
      "European Offshore Wind Market development, CBAM: regulations and compliance for the offshore wind sector",
  },
  {
    src: insightCBAMMedia[1],
    alt: "CBAM",
  },
  "CBAM: regulations and compliance for the offshore wind sector",
  "Understanding the nuances of the Carbon Border Adjustment Mechanism (CBAM) is crucial for all parties in the offshore wind supply chain, from non-EU steel exporters through to European developers. Here's an introduction to what you can expect from CBAM - and steps for staying compliant. ",
  `The Carbon Border Adjustment Mechanism (CBAM) is a sea change for the offshore wind sector. It's complicated, it's disruptive and the implications are far reaching.`,
  <div>
    <strong>WeBridge Insights</strong>
    <h1>CBAM: regulations and compliance for the offshore wind sector</h1>
    <h2>
      Understanding the nuances of the Carbon Border Adjustment Mechanism (CBAM)
      is crucial for all parties in the offshore wind supply chain, from non-EU
      steel exporters through to European developers. Here's an introduction to
      what you can expect from CBAM - and steps for staying compliant.
    </h2>
    <p className="article-date">09 October 2025</p>
    <br />

    <p>
      The Carbon Border Adjustment Mechanism (CBAM) is a sea change for the
      offshore wind sector. It's complicated, it's disruptive and the
      implications are far reaching.
    </p>
    <p>
      In essence the CBAM places a duty on EU companies that import
      emissions-intensive goods - including steel - from non-EU countries.
    </p>
    <p>
      It's part of the "Fit for 55 in 2030" package: the EU's roadmap to reduce
      greenhouse gas emissions by at least 55% by 2030 compared to 1990 levels.
    </p>

    <h3>One quick thing before we start…</h3>
    <p>
      CBAM is a new and rapidly evolving policy. Many of the finer points are
      yet to be confirmed and uncertainty is widespread throughout the offshore
      wind sector. Get in touch for focused insight on how CBAM could affect
      your business - and where the opportunities are. As experts in connecting
      European developers with global steel suppliers, we are uniquely
      positioned to provide valuable input on the CBAM conversation.{" "}
      <a
        href="https://www.windenergybridge.com/contact-us"
        target="_blank"
        rel="noopener noreferrer"
      >
        Let's talk.
      </a>
    </p>

    <h3>Key provisions of the Carbon Border Adjustment Mechanism</h3>
    <p>The CBAM has four central aims:</p>
    <ul>
      <li>Reduce carbon leakage</li>
      <li>Increase the prosperity and resilience of European industry</li>
      <li>Work towards domestic carbon reduction targets</li>
      <li>
        Encourage global participation with carbon reduction and cleaner
        industrial production
      </li>
    </ul>
    <br />
    <p>It covers the production of emissions-intensive goods, including: </p>
    <ul>
      <li>Aluminium</li>
      <li>Steel</li>
      <li>Iron</li>
      <li>Cement </li>
      <li>Fertilisers</li>
      <li>Hydrogen</li>
    </ul>
    <br />
    <p>
      Think of the CBAM as a balancing act on the fees imposed for excess
      emissions production. European steel producers are already taxed for their
      emissions under the EU ETS. The CBAM seeks to ensure companies importing
      steel from non-EU counties face an emissions penalty that's in line with
      what's already paid by European manufacturers.
    </p>
    <p>
      Carbon credits will not be eligible to reduce the CBAM duties. However if
      a non-EU producer can show they have already paid a price for the
      emissions embedded in the production of the imported goods, the
      corresponding cost can be fully deducted for the EU importer. We expand on
      the ambitions of the CBAM in{" "}
      <a
        href="https://www.windenergybridge.com/insights/carbon-border-adjustment-mechanism-cbam:-what-does-it-mean-for-importing-offshore-wind-infrastructure-from-china"
        target="_blank"
        rel="noopener noreferrer"
      >
        this article
      </a>
      .
    </p>
    <p>
      It's also worth noting that the UK has plans to impose its own CBAM
      regulations, targeting the aluminium, cement, fertiliser, hydrogen and
      iron and steel sectors. The UK CBAM is due to come into force from 1
      <sup>st</sup>
      January 2027. You can find out more{" "}
      <a
        href="https://www.gov.uk/government/publications/factsheet-carbon-border-adjustment-mechanism-cbam/factsheet-carbon-border-adjustment-mechanism"
        target="_blank"
        rel="noopener noreferrer"
      >
        here
      </a>
      .
    </p>

    <h3>Is the CBAM a threat to Europe's offshore wind targets?</h3>
    <p>Okay, here's a key talking point. </p>
    <p>
      On paper, the CBAM seems like a fair and common-sense policy - promoting a
      level playing field between domestic and non-domestic producers of steel
      manufactured for European markets. But disincentivising the importation of
      steel from non-EU countries could have serious consequences for Europe's
      offshore wind ambitions and ambitious decarbonisation targets.
    </p>
    <p>
      To quote from our{" "}
      <a
        href="https://www.windenergybridge.com/insights/carbon-border-adjustment-mechanism-cbam:-what-does-it-mean-for-importing-offshore-wind-infrastructure-from-china"
        target="_blank"
        rel="noopener noreferrer"
      >
        previous article on the CBAM
      </a>
      :
    </p>

    <p className="ml-10 italic">
      European nations have{" "}
      <a
        href="https://www.windenergybridge.com/insights/why-europe-needs-new-entrants-into-the-supply-chain-for-offshore-wind"
        target="_blank"
        rel="noopener noreferrer"
      >
        bold plans
      </a>{" "}
      to move from 41 GW of offshore wind capacity to 207 GW by 2035. That
      requires steel. Lots of it. Yet{" "}
      <a
        href="https://www.windenergybridge.com/insights/why-europe-needs-new-entrants-into-the-supply-chain-for-offshore-wind"
        target="_blank"
        rel="noopener noreferrer"
      >
        research shows
      </a>{" "}
      that demand for offshore wind installations is rapidly surpassing the
      European supply chain's current capacity. There's an argument that by
      making it more expensive to import steel from China, the CBAM further
      fuels the inflation in the UK and Europe that has been{" "}
      <a
        href="https://www.windenergybridge.com/insights/keeping-offshore-wind-developments-on-track-in-a-time-of-expensive-capital"
        target="_blank"
        rel="noopener noreferrer"
      >
        depressing the economic viability
      </a>{" "}
      of offshore wind development. Perhaps in a time of domestic shortfall we
      should legislate to make it easier to import Chinese steel, not harder.
      Contrast the CBAM, for example, with the{" "}
      <a
        href="https://en.wikipedia.org/wiki/Regional_Comprehensive_Economic_Partnership"
        target="_blank"
        rel="noopener noreferrer"
      >
        RCEP
      </a>{" "}
      - a new free trade agreement between China and 14 other Asia-Pacific
      nations.
    </p>

    <p>
      China - along with other major steel exporters - remains crucial to
      Europe's offshore wind ambitions. In 2024 alone the EU{" "}
      <a
        href="https://www.trade.gov/media/5479"
        target="_blank"
        rel="noopener noreferrer"
      >
        imported
      </a>{" "}
      over 38 million metric tonnes of steel, from dozens of different
      countries. Indeed, the EU is the world's largest steel importer.
      <sup>1</sup>
    </p>
    <p>
      By taking steps now to enhance their environmental credentials, Chinese
      and other non-EU steel manufacturers and fabricators can position
      themselves favourably to strengthen their position in a European market
      adjusting to the regulatory rigour of the CBAM. In short: the cleaner your
      production, the more attractive you are to EU developers.
    </p>
    <p>
      Even so, the International Chamber of Shipping{" "}
      <a
        href="https://www.ics-shipping.org/news-item/carbon-mechanism-could-knock-12-off-eu-industrial-imports/"
        target="_blank"
        rel="noopener noreferrer"
      >
        anticipates
      </a>{" "}
      that the CBAM will reduce the combined import value of iron, steel,
      cement, fertilisers, aluminium, electricity and hydrogen into Europe by
      11.9% by 2030.
      <sup>2</sup>
    </p>

    <h3>When will the CBAM be implemented in the EU?</h3>
    <p>
      The CBAM is already in the process of being phased in. After a
      transitional period, the CBAM regulations will be rolled out in full from
      1<sup>st</sup> January 2026.
    </p>
    <p>Here's a breakdown.</p>

    <p>
      <b>
        Transitional phase: 1<sup>st</sup> October 2023 - 31<sup>st</sup>{" "}
        December 2025
      </b>
    </p>
    <ul>
      <li>Quarterly emissions reporting required</li>
      <li>
        No financial adjustments for embedded emissions, but penalties may be
        imposed for non-compliance
      </li>
    </ul>
    <br />

    <p>
      <b>
        Definitive phase: from 1<sup>st</sup> January 2026
      </b>
    </p>
    <ul>
      <li>Verification requirements come into force</li>
      <li>Gradual phase-in of CBAM duties</li>
      <li>By 2034 100% of embedded emissions of CBAM goods will be covered</li>
    </ul>
    <br />

    <p>
      From 1<sup>st</sup> January 2026, importers of goods covered by the CBAM
      are required to buy CBAM certificates to offset the emissions embedded in
      the goods they've imported. The price of these certificates will be linked
      to the weekly average auction price of EU Emissions Trading System (ETS)
      allowances. This ensures the emission fees paid by importers of CBAM goods
      aligns with the way domestic manufacturers are charged for their
      emissions.
    </p>
    <p>
      The CBAM duties are set to be phased in gradually between 2026 and 2034.
    </p>
    <div className="overflow-x-scroll">
      <table
        style={{
          borderCollapse: "collapse",
          width: "100%",
          margin: "20px 0",
          minWidth: "800px",
        }}
      >
        <caption
          style={{
            captionSide: "top",
            textAlign: "left",
            fontWeight: 600,
            marginBottom: "0.5em",
          }}
        >
          CBAM Free Allowance Phase-Out Schedule (2026–2034)
        </caption>
        <thead>
          <tr className="text-left">
            <th scope="col">Year</th>
            <th scope="col">2026</th>
            <th scope="col">2027</th>
            <th scope="col">2028</th>
            <th scope="col">2029</th>
            <th scope="col">2030</th>
            <th scope="col">2031</th>
            <th scope="col">2032</th>
            <th scope="col">2033</th>
            <th scope="col">2034</th>
          </tr>
        </thead>
        <tbody>
          <tr className="text-left">
            <td>
              <b>Free allowances</b>
            </td>
            <td>97.5%</td>
            <td>95.0%</td>
            <td>90.0%</td>
            <td>77.5%</td>
            <td>51.5%</td>
            <td>39.0%</td>
            <td>26.5%</td>
            <td>14.0%</td>
            <td>0.0%</td>
          </tr>
        </tbody>
      </table>
    </div>

    <h3>How does the CBAM apply to offshore wind infrastructure?</h3>
    <p>Many iron and steel items are covered by the CBAM, including: </p>
    <ul>
      <li>Agglomerated ores</li>
      <li>Pig iron</li>
      <li>Crude steel</li>
      <li>
        Finished and semi-finished products - such as bars, rods, rails, wires,
        tubes, plates, tanks, drums, nuts and bolts etc
      </li>
      <li>Scrap and remelted steel</li>
    </ul>
    <p>
      This has the potential to cover many steel and iron-based offshore wind
      components - including monopiles, jacket structures and towers, which fall
      under Chapter 73 of the{" "}
      <a
        href="https://taxation-customs.ec.europa.eu/customs/calculation-customs-duties/customs-tariff/combined-nomenclature_en"
        target="_blank"
        rel="noopener noreferrer"
      >
        Combined Nomenclature (CN)
      </a>{" "}
      categories.
      <sup>3</sup>
    </p>
    <p>
      Semi-finished steel and major fabricated sections used in offshore wind
      structures are also covered - such as steel plates, tubes, lattice masts
      and pillars. Extensions to what's covered by the CBAM are expected by
      2030.
    </p>
    <p>
      You can find out more about what steel products are covered by the CBAM{" "}
      <a
        href="https://taxation-customs.ec.europa.eu/carbon-border-adjustment-mechanism/cbam-information-sector_en#iron-and-steel"
        target="_blank"
        rel="noopener noreferrer"
      >
        here
      </a>
      .
    </p>

    <h3>What does the CBAM mean for non-EU steel exporters? </h3>
    <p>
      The CBAM gives non-EU steel exporters pause for thought. While they are
      not financially liable for the cost of CBAM certificates, there is a
      potential commercial impact via reduced price competitiveness in the EU
      market.
    </p>
    <p>
      There is also an obligation to provide clear and accurate data on the
      emissions embedded in the production of goods sold to EU customers.
      Without it, customers will be forced to use default data that uses
      conservative estimates to calculate emissions. This will likely result in
      importers paying over the odds for CBAM certificates.
    </p>
    <p>
      But it's also clear that the CBAM presents a commercial and strategic
      opportunity for steel exporters. Those who innovate to cut emissions and
      provide a more environmentally-friendly product are likely to gain
      significant market share in Europe thanks to the CBAM rules. In short: the
      CBAM incentivises low emission steel production and should spur investment
      in green technologies.
    </p>

    <h3>The CBAM and your business…</h3>
    <p>
      For targeted insight on how the CBAM will impact your business,{" "}
      <a
        href="https://www.windenergybridge.com/contact-us"
        target="_blank"
        rel="noopener noreferrer"
      >
        get in touch
      </a>
      . Or find out more about{" "}
      <a
        href="https://www.windenergybridge.com/about-us"
        target="_blank"
        rel="noopener noreferrer"
      >
        our work
      </a>
      .
    </p>

    <h3>References </h3>
    <ol className="list-decimal ml-6">
      <li>
        The International Trade Administration:{" "}
        <a
          href="https://www.trade.gov/media/5479"
          target="_blank"
          rel="noopener noreferrer"
        >
          Steel Trade Balance
        </a>
        .
      </li>
      <li>
        The International Chamber of Shipping:{" "}
        <a
          href="https://www.ics-shipping.org/news-item/carbon-mechanism-could-knock-12-off-eu-industrial-imports/"
          target="_blank"
          rel="noopener noreferrer"
        >
          Carbon mechanism could knock 12% off EU industrial imports
        </a>
        .
      </li>
      <li>
        European Commission:{" "}
        <a
          href="https://taxation-customs.ec.europa.eu/customs/calculation-customs-duties/customs-tariff/combined-nomenclature_en"
          target="_blank"
          rel="noopener noreferrer"
        >
          The Combined Nomenclature
        </a>
        .
      </li>
    </ol>

    <br />
    <br />
    <br />
    <p>
      As the global offshore wind sector continues to advance, innovation,
      investments, and supportive policies are driving transformative growth. At{" "}
      <i>
        <b>WeBridge</b>
      </i>
      , we provide the expertise and insights our partners need to confidently
      navigate this evolving landscape.
    </p>
    <h3 style={{ marginTop: "200px" }}>Subscribe</h3>
    <ContactSubscribe />
  </div>,
);
